import React, { useState, useEffect } from 'react';
import { X, Check, Crown, Zap, Shield, Star } from 'lucide-react';
import { plansService } from '../services/plans-service';
import { getStripeStatus } from '../services/stripe-service';
import { Plan, Subscription } from '../types/user';

interface PlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  currentPlan?: string;
}

export default function PlansModal({ isOpen, onClose, tenantId, currentPlan }: PlansModalProps) {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      setStripeStatus(getStripeStatus());
    }
  }, [isOpen]);

  const loadPlans = async () => {
    try {
      const plansData = await plansService.getPlans();
      setPlans(plansData);
    } catch (error) {
      console.error('❌ Erro ao carregar planos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planSlug: string, billingCycle: 'monthly' | 'yearly' = 'monthly') => {
    setUpgrading(true);
    try {
      console.log(`⬆️ Fazendo upgrade para ${planSlug}...`);
      
      if (planSlug === 'free') {
        // Simular downgrade para free
        console.log('✅ Simulando downgrade para Free');
        alert('✅ Downgrade para Free realizado! (Simulado)');
        onClose();
        return;
      }

      // Para planos Premium - sempre simular por enquanto
      if (planSlug === 'premium') {
        // Tentar upgrade real primeiro, com fallback para simulação
        try {
          if (stripeStatus?.isConfigured) {
            console.log('💳 Tentando criar checkout Stripe...');
            const checkoutSession = await plansService.createStripeCheckout(tenantId, planSlug, billingCycle);
            
            // Abrir checkout Stripe em nova aba
            window.open(checkoutSession.url, '_blank');
            return;
          } else {
            // Sem Stripe configurado - simular upgrade
            console.log('🎭 Stripe não configurado, simulando upgrade...');
            throw new Error('Stripe não configurado');
          }
        } catch (upgradeError) {
          console.log('⚠️ Erro no upgrade real, usando simulação:', upgradeError);
          
          // FALLBACK: Upgrade simulado
          console.log('🎭 Executando upgrade simulado para Premium...');
          
          // Salvar no localStorage para simular plano Premium
          const currentPlan = {
            tenantId: tenantId,
            plan: 'premium',
            billingCycle: billingCycle,
            upgradeDate: new Date().toISOString(),
            isSimulated: true
          };
          
          localStorage.setItem(`plan_${tenantId}`, JSON.stringify(currentPlan));
          
          alert(`🎉 Upgrade para Premium ${billingCycle === 'yearly' ? 'Anual' : 'Mensal'} realizado!
          
✨ Agora você tem:
• Mensagens ilimitadas
• Todas as funcionalidades
• Suporte prioritário

💡 Este é um upgrade simulado. Em produção, seria processado pelo Stripe.`);
          
          onClose();
          window.location.reload();
          return;
        }
      }

    } catch (error) {
      console.error('❌ Erro ao fazer upgrade:', error);
      alert(`❌ Erro ao atualizar plano: ${error.message || 'Erro desconhecido'}
      
🔧 Detalhes técnicos foram logados no console (F12).`);
    } finally {
      setUpgrading(false);
    }
  };

  const getPlanIcon = (planSlug: string) => {
    switch (planSlug) {
      case 'free':
        return <Shield className="w-8 h-8 text-gray-500" />;
      case 'premium':
        return <Crown className="w-8 h-8 text-yellow-500" />;
      default:
        return <Star className="w-8 h-8 text-blue-500" />;
    }
  };

  const getPlanColor = (planSlug: string) => {
    switch (planSlug) {
      case 'free':
        return 'border-gray-300 bg-gray-50';
      case 'premium':
        return 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50';
      default:
        return 'border-blue-300 bg-blue-50';
    }
  };

  const formatPrice = (price: number, cycle: 'monthly' | 'yearly') => {
    if (price === 0) return 'Gratuito';
    
    const formattedPrice = price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    });

    return `${formattedPrice}/${cycle === 'monthly' ? 'mês' : 'ano'}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Escolha seu Plano</h2>
            <p className="text-gray-600 mt-1">Desbloqueie o potencial completo da IA para sua empresa</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>


        {/* Planos */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="max-w-md w-full">
              {plans.filter(plan => plan.slug === 'premium').map((plan) => {
                const isPremium = plan.slug === 'premium';
                
                return (
                  <div
                    key={plan.id}
                    className={`relative p-6 rounded-xl border-2 transition-all duration-200 ${getPlanColor(plan.slug)} ${
                      isPremium ? 'transform scale-105 shadow-lg' : ''
                    }`}
                  >
                    {/* Badge Premium */}
                    {isPremium && (
                      <div className="absolute -top-3 right-4">
                        <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                          <Zap className="w-3 h-3 mr-1" />
                          Mais Popular
                        </span>
                      </div>
                    )}

                    {/* Cabeçalho do plano */}
                    <div className="text-center mb-6">
                      <div className="flex justify-center mb-3">
                        {getPlanIcon(plan.slug)}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <p className="text-gray-600 text-sm mb-4">{plan.description}</p>
                      
                      {/* Preço */}
                      <div className="mb-4">
                        <div className="text-3xl font-bold text-gray-900">
                          {formatPrice(plan.priceMonthly, 'monthly')}
                        </div>
                        {plan.priceYearly > 0 && (
                          <div className="text-sm text-gray-500 mt-1">
                            ou {formatPrice(plan.priceYearly, 'yearly')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-6">
                      {/* Limite de mensagens */}
                      <div className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">
                          {plan.dailyMessageLimit ? (
                            <><strong>{plan.dailyMessageLimit} mensagens</strong> por dia</>
                          ) : (
                            <><strong>Mensagens ilimitadas</strong> por dia</>
                          )}
                        </span>
                      </div>

                      {/* Features do plano */}
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Botão de ação */}
                    <div className="space-y-2">
                      {/* Botão mensal */}
                      <button
                        onClick={() => handleUpgrade(plan.slug, 'monthly')}
                        disabled={upgrading}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                          isPremium
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white shadow-lg'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        } ${upgrading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {upgrading ? 'Processando...' : 'Assinar Mensal'}
                      </button>

                      {/* Botão anual (só para planos pagos) */}
                      {plan.priceYearly > 0 && (
                        <button
                          onClick={() => handleUpgrade(plan.slug, 'yearly')}
                          disabled={upgrading}
                          className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
                        >
                          Assinar Anual (Economize!)
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-6 bg-gray-50">
          <div className="text-center text-sm text-gray-600">
            <p>💳 Processamento seguro via Stripe</p>
            <p className="mt-1">🔒 SSL · PCI DSS Compliant · Criptografia de ponta a ponta</p>
            <p className="mt-1">📞 Suporte: contato@spaceai.com.br</p>
          </div>
        </div>
      </div>
    </div>
  );
}
